import * as React from 'react';

import type { UploadDryRunPreview } from '@/api/catalog-client';
import {
  useCancelUpload,
  useConfirmUpload,
  useUploadDryRun,
} from '@/api/use-catalog-edition';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  anomalySeverityLabel,
  parsingStatusLabel,
  validationStatusLabel,
} from '@/lib/catalog-labels';
import { cn } from '@/lib/utils';

// =============================================================================
// catalog-upload-screen.tsx — A-10 upload Excel + dry-run (S9-D4).
// =============================================================================
// BC catalog, MONDE ADMIN d'ÉCRITURE (P-05). Upload d'un classeur Excel via une zone dédiée
// GLISSER-DÉPOSER (DBU §2.4 : « zone d'upload drag-and-drop dédiée ») construite avec les
// événements DOM natifs (0 dépendance) ET conservant le sélecteur fichier NATIF comme fallback
// clavier. → POST upload qui effectue un DRY-RUN : la réponse liste les anomalies PAR LIGNE et le
// statut de parsing SANS créer de version (GATE_HUMAN : « Dry-run = validation SANS création de
// version »). La version DRAFT n'est créée qu'à la CONFIRMATION explicite (bouton Confirmer).
//
// L'AUTORITÉ (RBAC, parsing, validation sémantique, inv.7 promotion REFUSÉE si anomalie
// bloquante, promotion atomique en DRAFT) est l'API ; l'UI relaie l'intention.
//
// WCAG 2.1 AA : <label> lié à l'input fichier (fallback clavier de la zone de dépôt) ; <table>
// <caption>/<th scope> pour les anomalies ; statuts rendus en LIBELLÉ texte (badge = renfort) ;
// messages role="status"/role="alert" ; navigation clavier native ; boutons d'action explicites.

/** Vrai si le fichier est un classeur .xlsx (extension OU type MIME OpenXML). */
function isXlsxFile(file: File): boolean {
  return (
    file.name.toLowerCase().endsWith('.xlsx') ||
    file.type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
}

export function CatalogUploadScreen(): React.JSX.Element {
  const upload = useUploadDryRun();
  const confirm = useConfirmUpload();
  const cancel = useCancelUpload();

  const [file, setFile] = React.useState<File | null>(null);
  /** Preview du dry-run courant (null = pas encore d'upload / abandonné). In-memory. */
  const [preview, setPreview] = React.useState<UploadDryRunPreview | null>(null);
  const [versionNumber, setVersionNumber] = React.useState('');
  const [producedVersionId, setProducedVersionId] = React.useState<string | null>(null);
  const [cancelled, setCancelled] = React.useState(false);
  /** Vrai pendant le survol d'un fichier au-dessus de la zone de dépôt (retour visuel). In-memory. */
  const [isDragging, setIsDragging] = React.useState(false);
  /** Message d'erreur de dépôt (type de fichier refusé côté saisie). In-memory. */
  const [dropError, setDropError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const resetDecisionState = React.useCallback((): void => {
    setProducedVersionId(null);
    setCancelled(false);
  }, []);

  /** Sélection d'un fichier (native OU dépôt) : réinitialise la preview et l'état de décision. */
  const acceptFile = React.useCallback(
    (next: File | null): void => {
      setDropError(null);
      setFile(next);
      setPreview(null);
      resetDecisionState();
    },
    [resetDecisionState],
  );

  const onDragOver = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      // preventDefault est REQUIS pour autoriser le drop (sinon le navigateur ouvre le fichier).
      event.preventDefault();
      setIsDragging(true);
    },
    [],
  );

  const onDragLeave = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      setIsDragging(false);
    },
    [],
  );

  const onDrop = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      setIsDragging(false);
      const dropped = event.dataTransfer.files[0] ?? null;
      if (dropped === null) {
        return;
      }
      if (!isXlsxFile(dropped)) {
        // Garde de saisie NON-autoritaire (le serveur reste l'autorité de parsing) : on refuse un
        // fichier manifestement non .xlsx AVANT l'envoi, avec un message explicite.
        setDropError('Seuls les classeurs Excel (.xlsx) sont acceptés.');
        setFile(null);
        setPreview(null);
        resetDecisionState();
        return;
      }
      acceptFile(dropped);
    },
    [acceptFile, resetDecisionState],
  );

  const onUpload = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (file === null) {
        return;
      }
      resetDecisionState();
      upload.mutate(file, {
        onSuccess: (result) => {
          setPreview(result);
        },
      });
    },
    [file, upload, resetDecisionState],
  );

  const onConfirm = React.useCallback((): void => {
    if (preview === null) {
      return;
    }
    confirm.mutate(
      { attemptId: preview.attemptId, versionNumber },
      {
        onSuccess: (result) => {
          setProducedVersionId(result.producedVersionId);
          setPreview(null);
          setFile(null);
          setVersionNumber('');
          if (fileInputRef.current !== null) {
            fileInputRef.current.value = '';
          }
        },
      },
    );
  }, [confirm, preview, versionNumber]);

  const onCancel = React.useCallback((): void => {
    if (preview === null) {
      return;
    }
    cancel.mutate(preview.attemptId, {
      onSuccess: () => {
        setCancelled(true);
        setPreview(null);
        setFile(null);
        setVersionNumber('');
        if (fileInputRef.current !== null) {
          fileInputRef.current.value = '';
        }
      },
    });
  }, [cancel, preview]);

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Importer un catalogue (Excel)
        </h1>
        <p className="text-muted-foreground">
          L'import effectue d'abord une validation à blanc : les anomalies sont affichées
          AVANT toute création. Aucune version n'est créée tant que vous n'avez pas
          confirmé.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fichier source</CardTitle>
          <CardDescription>
            Sélectionnez un classeur Excel (.xlsx) — 3 feuilles nommées Profiles
            (obligatoire), Correlations, Skills. L'envoi déclenche la validation à
            blanc (dry-run), sans création de version.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onUpload} className="space-y-4">
            <div className="space-y-2">
              {/*
                Zone de dépôt (DBU §2.4 : « zone d'upload drag-and-drop dédiée »). Le glisser-déposer
                est une AMÉLIORATION ; le <label>/<input> natif reste le fallback pleinement pilotable
                au clavier (WCAG 2.1 AA — aucune fonction réservée à la souris). Le bord change à la
                fois de couleur ET de style (dashed → solid) pour ne pas dépendre de la seule teinte.
              */}
              <div
                onDragOver={onDragOver}
                onDragEnter={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={cn(
                  'space-y-2 rounded-lg border-2 border-dashed border-input bg-muted/20 p-6 text-center transition-colors',
                  isDragging && 'border-solid border-primary bg-primary/5',
                )}
              >
                <p className="text-sm font-medium text-foreground">
                  Glissez-déposez un classeur Excel (.xlsx) ici
                </p>
                <p className="text-xs text-muted-foreground">
                  ou sélectionnez un fichier depuis votre ordinateur :
                </p>
                <div className="mx-auto max-w-sm space-y-2 text-left">
                  <Label htmlFor="upload-file">Classeur Excel</Label>
                  <Input
                    id="upload-file"
                    ref={fileInputRef}
                    type="file"
                    name="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => {
                      acceptFile(e.target.files?.[0] ?? null);
                    }}
                  />
                </div>
                <div aria-live="polite" aria-atomic="true">
                  {file !== null ? (
                    <p role="status" className="text-xs text-foreground">
                      Fichier sélectionné : {file.name}.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div aria-live="polite" aria-atomic="true">
              {dropError !== null ? (
                <p role="alert" className="text-sm text-destructive">
                  {dropError}
                </p>
              ) : null}
              {upload.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  {upload.error.message}
                </p>
              ) : null}
            </div>

            <Button type="submit" disabled={file === null || upload.isPending}>
              {upload.isPending ? 'Validation à blanc…' : 'Valider à blanc'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Accusés de décision (post-confirmation / post-annulation). */}
      <div aria-live="polite" aria-atomic="true">
        {producedVersionId !== null ? (
          <p
            role="status"
            className="rounded-md border border-success/40 bg-success/10 p-4 text-sm font-medium text-success"
          >
            Version brouillon créée : {producedVersionId}.
          </p>
        ) : null}
        {cancelled ? (
          <p role="status" className="text-sm text-muted-foreground">
            Import annulé. Aucune version n'a été créée.
          </p>
        ) : null}
      </div>

      {preview !== null ? (
        <DryRunPreview
          preview={preview}
          versionNumber={versionNumber}
          onVersionNumberChange={setVersionNumber}
          onConfirm={onConfirm}
          onCancel={onCancel}
          confirmPending={confirm.isPending}
          cancelPending={cancel.isPending}
          confirmError={confirm.isError ? confirm.error.message : null}
        />
      ) : (
        !upload.isPending &&
        producedVersionId === null &&
        !cancelled && (
          <p role="status" className="text-muted-foreground">
            Aucun import en attente. Sélectionnez un fichier pour lancer une validation à
            blanc.
          </p>
        )
      )}
    </div>
  );
}

function DryRunPreview({
  preview,
  versionNumber,
  onVersionNumberChange,
  onConfirm,
  onCancel,
  confirmPending,
  cancelPending,
  confirmError,
}: {
  readonly preview: UploadDryRunPreview;
  readonly versionNumber: string;
  readonly onVersionNumberChange: (value: string) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly confirmPending: boolean;
  readonly cancelPending: boolean;
  readonly confirmError: string | null;
}): React.JSX.Element {
  const cannotConfirm =
    preview.hasBlockingAnomalies || versionNumber.trim().length === 0;

  return (
    <Card aria-labelledby="dry-run-heading">
      <CardHeader>
        <CardTitle id="dry-run-heading" className="text-base">
          Validation à blanc — {preview.sourceFilename}
        </CardTitle>
        <CardDescription>
          Résultat de la validation. Aucune version n'a été créée à ce stade. Confirmez
          pour produire une version brouillon, ou annulez pour abandonner.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Statuts (libellé porteur d'info, badge en renfort). */}
        <dl className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <dt className="text-sm text-muted-foreground">Lecture du fichier</dt>
            <dd className="text-sm font-medium text-foreground">
              {parsingStatusLabel(preview.parsingStatus)}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm text-muted-foreground">Validation</dt>
            <dd className="text-sm font-medium text-foreground">
              {validationStatusLabel(preview.validationStatus)}
            </dd>
          </div>
        </dl>

        {/* Bilan des changements détectés. */}
        <section aria-labelledby="change-summary-heading" className="space-y-2">
          <h3
            id="change-summary-heading"
            className="text-sm font-medium text-foreground"
          >
            Changements détectés
          </h3>
          <ul className="grid gap-1 text-sm text-foreground sm:grid-cols-2">
            <li>Profils ajoutés : {preview.changeSummary.profilesAdded}</li>
            <li>Profils modifiés : {preview.changeSummary.profilesModified}</li>
            <li>Profils retirés : {preview.changeSummary.profilesRemoved}</li>
            <li>Compétences ajoutées : {preview.changeSummary.skillsAdded}</li>
            <li>Compétences modifiées : {preview.changeSummary.skillsModified}</li>
            <li>Compétences retirées : {preview.changeSummary.skillsRemoved}</li>
          </ul>
        </section>

        {/* Anomalies par ligne. */}
        <section aria-labelledby="anomalies-heading" className="space-y-2">
          <h3 id="anomalies-heading" className="text-sm font-medium text-foreground">
            Anomalies détectées
          </h3>
          {preview.anomalies.length === 0 ? (
            <p role="status" className="text-sm text-muted-foreground">
              Aucune anomalie détectée.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="ds-table w-full border-collapse text-sm">
                <caption className="sr-only">
                  Anomalies de validation (ligne, sévérité, code, message)
                </caption>
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                    <th scope="col" className="px-4 py-3 font-medium">
                      Ligne
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Sévérité
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Code
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Message
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {preview.anomalies.map((anomaly, index) => {
                    const sev = anomalySeverityLabel(anomaly.severity);
                    return (
                      <tr
                        key={`${anomaly.code}-${String(anomaly.row)}-${String(index)}`}
                        className="border-b border-border last:border-b-0"
                      >
                        <td className="px-4 py-3 align-top">
                          {anomaly.row === null ? (
                            <span
                              className="text-muted-foreground"
                              aria-label="Anomalie globale"
                            >
                              Globale
                            </span>
                          ) : (
                            anomaly.row
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {/* Badge = renfort ; le LIBELLÉ porte l'information. */}
                          <Badge variant={sev.variant}>{sev.label}</Badge>
                        </td>
                        <td className="px-4 py-3 align-top font-mono">
                          {anomaly.code}
                        </td>
                        <td className="px-4 py-3 align-top">{anomaly.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {preview.hasBlockingAnomalies ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm font-medium text-destructive"
          >
            La promotion en brouillon est refusée tant qu'au moins une anomalie bloquante
            subsiste. Corrigez le fichier et relancez une validation.
          </p>
        ) : null}

        {/* Confirmation explicite (création de la version DRAFT). */}
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="confirm-version-number">Numéro de version</Label>
            <Input
              id="confirm-version-number"
              name="versionNumber"
              className="font-mono"
              placeholder="Ex. : 2026.04"
              value={versionNumber}
              onChange={(e) => onVersionNumberChange(e.target.value)}
              disabled={preview.hasBlockingAnomalies}
            />
          </div>

          <div aria-live="polite" aria-atomic="true">
            {confirmError !== null ? (
              <p role="alert" className="text-sm text-destructive">
                {confirmError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              onClick={onConfirm}
              disabled={cannotConfirm || confirmPending}
            >
              {confirmPending ? 'Confirmation…' : 'Confirmer et créer le brouillon'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={cancelPending}
            >
              {cancelPending ? 'Annulation…' : 'Annuler'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
